import React, { useMemo, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    ButtonBase,
    ButtonGroup,
    Chip,
    InputAdornment,
    LinearProgress,
    makeStyles,
    Paper,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import VisibilityIcon from '@material-ui/icons/Visibility'
import HomeWorkIcon from '@material-ui/icons/HomeWork'
import CategoryIcon from '@material-ui/icons/Category'
import SearchIcon from '@material-ui/icons/Search'
import { DocumentPreviewModal } from '../../../ContractManager/DocumentPreviewModal'
import { formatDate } from '../../../ContractManager/utils'
import { PivotedPropertyGroup } from './coverageFilters'

export type CoverageGrouping = 'byType' | 'byProperty'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
    },
    sectionToolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    propertySearch: {
        minWidth: 260,
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.background.paper,
        },
    },
    viewModeToggle: {
        marginLeft: 'auto',
        flexShrink: 0,
    },
    viewModeButton: {
        textTransform: 'none',
        fontWeight: 600,
    },
    emptyState: {
        padding: theme.spacing(6, 3),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        borderRadius: theme.spacing(1.5),
    },
    emptyIcon: {
        fontSize: 40,
        color: theme.palette.text.disabled,
        marginBottom: theme.spacing(0.5),
    },
    accordion: {
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        borderRadius: theme.spacing(1.5),
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        '&:before': {
            display: 'none',
        },
        '& .MuiAccordionSummary-root': {
            padding: theme.spacing(0.5, 2),
        },
        '& .MuiAccordionSummary-content': {
            margin: theme.spacing(1.25, 0),
            alignItems: 'center',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
            margin: theme.spacing(1.25, 0),
        },
    },
    accordionAttention: {
        borderColor: '#fecaca',
    },
    summaryContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        width: '100%',
        minWidth: 0,
    },
    propertyTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        minWidth: 0,
        flex: '1 1 auto',
    },
    propertyMark: {
        width: 34,
        height: 34,
        borderRadius: 10,
        backgroundColor: '#eff6ff',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    propertyMarkAttention: {
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
    },
    propertyIcon: {
        fontSize: 18,
    },
    propertyCopy: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: 0,
        flex: '1 1 auto',
    },
    propertyName: {
        fontWeight: 600,
        lineHeight: 1.3,
    },
    propertyMeta: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    stats: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexShrink: 0,
    },
    progressSummary: {
        width: 108,
    },
    progressBar: {
        height: 6,
        borderRadius: 999,
        backgroundColor: theme.palette.grey[200],
        '& .MuiLinearProgress-bar': {
            borderRadius: 999,
        },
    },
    statusChip: {
        width: 168,
        height: 24,
        fontWeight: 600,
        justifyContent: 'center',
        '& .MuiChip-label': {
            width: '100%',
            paddingLeft: 8,
            paddingRight: 8,
            textAlign: 'center',
        },
    },
    gapChip: {
        backgroundColor: '#fef2f2',
        color: '#991b1b',
    },
    optionalGapChip: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.secondary,
    },
    completeChip: {
        backgroundColor: '#f0fdf4',
        color: '#166534',
    },
    details: {
        display: 'block',
        padding: theme.spacing(0, 2, 2),
    },
    split: {
        display: 'flex',
        alignItems: 'stretch',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    panel: {
        flex: '1 1 280px',
        minWidth: 0,
        borderRadius: 12,
        padding: theme.spacing(1.25),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    panelMissing: {
        backgroundColor: '#fff7f7',
        border: '1px solid #fecaca',
    },
    panelOnFile: {
        backgroundColor: '#f0fdf4',
        border: '1px solid #bbf7d0',
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        padding: theme.spacing(0.25, 0.5),
    },
    panelTitleMissing: {
        fontSize: 13,
        fontWeight: 700,
        color: '#991b1b',
    },
    panelTitleOnFile: {
        fontSize: 13,
        fontWeight: 700,
        color: '#166534',
    },
    panelCount: {
        minWidth: 22,
        height: 22,
        padding: theme.spacing(0, 0.75),
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        backgroundColor: theme.palette.background.paper,
    },
    panelCountMissing: {
        color: '#991b1b',
    },
    panelCountOnFile: {
        color: '#166534',
    },
    typeList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    typeCard: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 8,
        border: '1px solid rgba(15, 23, 42, 0.06)',
        padding: theme.spacing(1, 1.25),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
    },
    typeMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    typeName: {
        fontWeight: 600,
        minWidth: 0,
    },
    requiredChip: {
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
        flexShrink: 0,
    },
    emptyPanel: {
        padding: theme.spacing(1.5, 0.5),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: 13,
    },
    documentLinks: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    documentLink: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        width: '100%',
        padding: theme.spacing(0.75, 1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: '#f8fafc',
        textAlign: 'left',
        cursor: 'pointer',
    },
    documentName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontWeight: 500,
    },
}))

interface CoverageGroupingToggleProps {
    value: CoverageGrouping
    onChange: (value: CoverageGrouping) => void
}

export const CoverageGroupingToggle: React.FC<CoverageGroupingToggleProps> = ({
    value,
    onChange,
}) => {
    const classes = useStyles()
    return (
        <ButtonGroup
            className={classes.viewModeToggle}
            color="primary"
            size="small"
        >
            <Button
                className={classes.viewModeButton}
                variant={value === 'byType' ? 'contained' : 'outlined'}
                startIcon={<CategoryIcon fontSize="small" />}
                onClick={() => onChange('byType')}
            >
                By type
            </Button>
            <Button
                className={classes.viewModeButton}
                variant={value === 'byProperty' ? 'contained' : 'outlined'}
                startIcon={<HomeWorkIcon fontSize="small" />}
                onClick={() => onChange('byProperty')}
            >
                By property
            </Button>
        </ButtonGroup>
    )
}

interface CoveragePropertyViewProps {
    propertyGroups: PivotedPropertyGroup[]
    itemNoun: string
}

export const CoveragePropertyView: React.FC<CoveragePropertyViewProps> = ({
    propertyGroups,
    itemNoun,
}) => {
    const classes = useStyles()
    const [propertySearch, setPropertySearch] = useState('')
    const [previewDoc, setPreviewDoc] = useState<{
        title: string
        fileUrl: string
    } | null>(null)
    const visibleGroups = useMemo(() => {
        const search = propertySearch.trim().toLowerCase()
        if (!search) return propertyGroups
        return propertyGroups.filter((group) =>
            group.propertyName.toLowerCase().includes(search),
        )
    }, [propertyGroups, propertySearch])

    const fileNoun =
        itemNoun === 'document'
            ? 'documents'
            : itemNoun === 'certificate'
            ? 'certificates'
            : 'contracts'

    return (
        <Box className={classes.root}>
            <Box className={classes.sectionToolbar}>
                <Typography variant="body2" color="textSecondary">
                    Showing {visibleGroups.length} of {propertyGroups.length}{' '}
                    properties
                </Typography>
                <TextField
                    className={classes.propertySearch}
                    placeholder="Search properties..."
                    variant="outlined"
                    size="small"
                    value={propertySearch}
                    onChange={(event) => setPropertySearch(event.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            {visibleGroups.length === 0 ? (
                <Paper variant="outlined" className={classes.emptyState}>
                    <HomeWorkIcon className={classes.emptyIcon} />
                    <Typography variant="subtitle1">
                        {propertySearch.trim()
                            ? `No properties match "${propertySearch.trim()}"`
                            : 'No properties match these filters'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {propertySearch.trim()
                            ? 'Try a different property name.'
                            : 'Try widening the scope or clearing the active filters.'}
                    </Typography>
                </Paper>
            ) : (
                visibleGroups.map((group) => {
                    const percent =
                        group.totalCount === 0
                            ? 0
                            : Math.round(
                                  (group.onFileCount / group.totalCount) * 100,
                              )
                    const missingCount = group.totalCount - group.onFileCount
                    const missingTypes = group.types.filter(
                        (type) => !type.has_document,
                    )
                    const onFileTypes = group.types.filter(
                        (type) => type.has_document,
                    )
                    const gapLabel =
                        group.requiredMissingCount > 0
                            ? `${group.requiredMissingCount} required missing`
                            : `${missingCount} missing`
                    return (
                        <Accordion
                            key={group.propertyId}
                            className={`${classes.accordion} ${
                                group.requiredMissingCount > 0
                                    ? classes.accordionAttention
                                    : ''
                            }`}
                            defaultExpanded={false}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box className={classes.summaryContent}>
                                    <Box className={classes.propertyTitle}>
                                        <Box
                                            className={`${
                                                classes.propertyMark
                                            } ${
                                                group.requiredMissingCount > 0
                                                    ? classes.propertyMarkAttention
                                                    : ''
                                            }`}
                                        >
                                            <HomeWorkIcon
                                                className={classes.propertyIcon}
                                            />
                                        </Box>
                                        <Box className={classes.propertyCopy}>
                                            <Typography
                                                variant="subtitle1"
                                                className={classes.propertyName}
                                                noWrap
                                            >
                                                {group.propertyName}
                                            </Typography>
                                            <Typography
                                                className={classes.propertyMeta}
                                                noWrap
                                            >
                                                {group.onFileCount} of{' '}
                                                {group.totalCount} {fileNoun} on
                                                file
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box className={classes.stats}>
                                        <Box
                                            className={classes.progressSummary}
                                        >
                                            <LinearProgress
                                                className={classes.progressBar}
                                                variant="determinate"
                                                value={percent}
                                                color={
                                                    group.requiredMissingCount >
                                                    0
                                                        ? 'secondary'
                                                        : 'primary'
                                                }
                                            />
                                        </Box>
                                        {percent === 100 ? (
                                            <Chip
                                                size="small"
                                                label="Complete"
                                                className={`${classes.statusChip} ${classes.completeChip}`}
                                            />
                                        ) : missingCount > 0 ? (
                                            <Chip
                                                size="small"
                                                label={gapLabel}
                                                className={`${
                                                    classes.statusChip
                                                } ${
                                                    group.requiredMissingCount >
                                                    0
                                                        ? classes.gapChip
                                                        : classes.optionalGapChip
                                                }`}
                                            />
                                        ) : null}
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails className={classes.details}>
                                <Box className={classes.split}>
                                    <Box
                                        className={`${classes.panel} ${classes.panelMissing}`}
                                    >
                                        <Box className={classes.panelHeader}>
                                            <Typography
                                                className={
                                                    classes.panelTitleMissing
                                                }
                                            >
                                                Missing
                                            </Typography>
                                            <Typography
                                                className={`${classes.panelCount} ${classes.panelCountMissing}`}
                                            >
                                                {missingTypes.length}
                                            </Typography>
                                        </Box>
                                        {missingTypes.length === 0 ? (
                                            <Typography
                                                className={classes.emptyPanel}
                                            >
                                                Nothing missing
                                            </Typography>
                                        ) : (
                                            <Box className={classes.typeList}>
                                                {missingTypes.map((type) => (
                                                    <Box
                                                        key={
                                                            type.typeId ??
                                                            type.typeName
                                                        }
                                                        className={
                                                            classes.typeCard
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                classes.typeMeta
                                                            }
                                                        >
                                                            <Tooltip
                                                                title={
                                                                    type.typeDescription ||
                                                                    ''
                                                                }
                                                                disableHoverListener={
                                                                    !type.typeDescription
                                                                }
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    className={
                                                                        classes.typeName
                                                                    }
                                                                    noWrap
                                                                >
                                                                    {
                                                                        type.typeName
                                                                    }
                                                                </Typography>
                                                            </Tooltip>
                                                            {type.is_required && (
                                                                <Chip
                                                                    size="small"
                                                                    label="Required"
                                                                    className={
                                                                        classes.requiredChip
                                                                    }
                                                                />
                                                            )}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                    <Box
                                        className={`${classes.panel} ${classes.panelOnFile}`}
                                    >
                                        <Box className={classes.panelHeader}>
                                            <Typography
                                                className={
                                                    classes.panelTitleOnFile
                                                }
                                            >
                                                On file
                                            </Typography>
                                            <Typography
                                                className={`${classes.panelCount} ${classes.panelCountOnFile}`}
                                            >
                                                {onFileTypes.length}
                                            </Typography>
                                        </Box>
                                        {onFileTypes.length === 0 ? (
                                            <Typography
                                                className={classes.emptyPanel}
                                            >
                                                Nothing on file
                                            </Typography>
                                        ) : (
                                            <Box className={classes.typeList}>
                                                {onFileTypes.map((type) => (
                                                    <Box
                                                        key={
                                                            type.typeId ??
                                                            type.typeName
                                                        }
                                                        className={
                                                            classes.typeCard
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                classes.typeMeta
                                                            }
                                                        >
                                                            <Tooltip
                                                                title={
                                                                    type.typeDescription ||
                                                                    ''
                                                                }
                                                                disableHoverListener={
                                                                    !type.typeDescription
                                                                }
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    className={
                                                                        classes.typeName
                                                                    }
                                                                    noWrap
                                                                >
                                                                    {
                                                                        type.typeName
                                                                    }
                                                                </Typography>
                                                            </Tooltip>
                                                            {type.is_required && (
                                                                <Chip
                                                                    size="small"
                                                                    label="Required"
                                                                    className={
                                                                        classes.requiredChip
                                                                    }
                                                                />
                                                            )}
                                                        </Box>
                                                        {type.documents.length >
                                                            0 && (
                                                            <Box
                                                                className={
                                                                    classes.documentLinks
                                                                }
                                                            >
                                                                {type.documents.map(
                                                                    (doc) => (
                                                                        <Tooltip
                                                                            key={
                                                                                doc.id
                                                                            }
                                                                            title={`Uploaded ${formatDate(
                                                                                doc.created_date,
                                                                            )}`}
                                                                        >
                                                                            <ButtonBase
                                                                                className={
                                                                                    classes.documentLink
                                                                                }
                                                                                onClick={() =>
                                                                                    setPreviewDoc(
                                                                                        {
                                                                                            title:
                                                                                                doc.name,
                                                                                            fileUrl:
                                                                                                doc.document,
                                                                                        },
                                                                                    )
                                                                                }
                                                                            >
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    className={
                                                                                        classes.documentName
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        doc.name
                                                                                    }
                                                                                </Typography>
                                                                                <VisibilityIcon fontSize="small" />
                                                                            </ButtonBase>
                                                                        </Tooltip>
                                                                    ),
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )
                })
            )}
            <DocumentPreviewModal
                open={previewDoc != null}
                onClose={() => setPreviewDoc(null)}
                title={previewDoc?.title || 'Document'}
                fileUrl={previewDoc?.fileUrl || null}
            />
        </Box>
    )
}
